import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Chip,
  Alert,
  List,
  ListItem,
  Divider,
  CircularProgress
} from '@mui/material'
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ClockIcon,
  CloudSync as SyncIcon,
  Cloud as CloudSyncIcon,
  NewReleases as NewIcon,
  Update as UpdateIcon,
  SkipNext as SkipIcon,
  Warning as WarningIcon
} from '@mui/icons-material'
import apiClient from '../../services/api'

interface SyncProgress {
  task_id: string
  status: string
  current_step: string | null
  progress_percent: number
  pacs_name: string
  institution_name: string | null
  message?: string  // Pentru status "queued"
  studies?: {
    total: number | null
    processed: number
    new: number
    updated: number
    skipped: number
    failed: number
  }
  performance?: {
    estimated_time_remaining_seconds: number | null
    average_time_per_study_ms: number | null
    duration_seconds: number | null
  }
  error_message: string | null
}

interface SyncProgressDialogProps {
  open: boolean
  taskIds: Array<{ task_id: string; pacs_name: string }>
  onClose: () => void
  onComplete: () => void
}

const SyncProgressDialog: React.FC<SyncProgressDialogProps> = ({ 
  open, 
  taskIds, 
  onClose, 
  onComplete 
}) => {
  const [progresses, setProgresses] = useState<Record<string, SyncProgress>>({})
  const [isCompleted, setIsCompleted] = useState(false)
  const [hasError, setHasError] = useState(false)

  // Reset state when taskIds change (new sync started)
  useEffect(() => {
    if (taskIds.length > 0) {
      console.log('🔄 New sync started - resetting state')
      setProgresses({})
      setIsCompleted(false)
      setHasError(false)
    }
  }, [taskIds])

  // Format time
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (minutes < 60) return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins}m`
  }

  // Fetch progress for all tasks
  const fetchProgress = async () => {
    try {
      const progressPromises = taskIds.map(async ({ task_id }) => {
        try {
          const response = await apiClient.get(`/studies/sync/progress/${task_id}`)
          return { task_id, data: response.data }
        } catch (err) {
          console.error(`Error fetching progress for task ${task_id}:`, err)
          return { task_id, data: null }
        }
      })

      const results = await Promise.all(progressPromises)
      
      const newProgresses: Record<string, SyncProgress> = {}
      let allCompleted = true
      let anyError = false

      results.forEach(({ task_id, data }) => {
        if (data) {
          newProgresses[task_id] = data
          if (data.status !== 'completed' && data.status !== 'failed') {
            allCompleted = false
          }
          if (data.status === 'failed') {
            anyError = true
          }
        }
      })

      setProgresses(newProgresses)
      
      if (allCompleted && Object.keys(newProgresses).length > 0 && !isCompleted) {
        setIsCompleted(true)
        setHasError(anyError)
        console.log('✅ All tasks completed!')
        onComplete()
      }
    } catch (err) {
      console.error('Error fetching sync progress:', err)
    }
  }

  // Poll for progress updates
  useEffect(() => {
    if (!open || taskIds.length === 0) return

    fetchProgress()

    const interval = setInterval(() => {
      if (!isCompleted) {
        fetchProgress()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [open, taskIds, isCompleted])

  // Get status display info
  const getStatusDisplay = (progress: SyncProgress) => {
    const { status, studies } = progress
    
    // Calculăm ce s-a întâmplat real
    const hasNew = (studies?.new || 0) > 0
    const hasUpdated = (studies?.updated || 0) > 0
    const hasSkipped = (studies?.skipped || 0) > 0
    const hasFailed = (studies?.failed || 0) > 0
    const totalProcessed = studies?.processed || 0
    
    switch (status) {
      case 'queued':
        return {
          icon: <ClockIcon />,
          color: 'warning' as const,
          label: 'În coadă',
          description: 'Task-ul așteaptă să înceapă...'
        }
      case 'fetching':
        return {
          icon: <CloudSyncIcon />,
          color: 'info' as const,
          label: 'Preluare',
          description: 'Se preiau informațiile despre studii din PACS...'
        }
      case 'processing':
        return {
          icon: <SyncIcon className="rotating" />,
          color: 'primary' as const,
          label: 'Procesare',
          description: `Se procesează studiile...`
        }
      case 'completed':
        // Determinăm mesajul corect bazat pe ce s-a întâmplat
        if (hasFailed && !hasNew && !hasUpdated) {
          return {
            icon: <ErrorIcon />,
            color: 'error' as const,
            label: 'Finalizat cu erori',
            description: `${studies?.failed} studii au eșuat`
          }
        }
        if (!hasNew && !hasUpdated && hasSkipped) {
          return {
            icon: <SkipIcon />,
            color: 'default' as const,
            label: 'Fără modificări',
            description: totalProcessed > 0 
              ? `${totalProcessed} studii verificate, toate erau deja sincronizate`
              : 'Nu au fost găsite studii noi'
          }
        }
        if (hasNew || hasUpdated) {
          return {
            icon: <CheckIcon />,
            color: 'success' as const,
            label: 'Sincronizat',
            description: `${hasNew ? `${studies?.new} noi` : ''}${hasNew && hasUpdated ? ', ' : ''}${hasUpdated ? `${studies?.updated} actualizate` : ''}`
          }
        }
        return {
          icon: <CheckIcon />,
          color: 'success' as const,
          label: 'Finalizat',
          description: 'Sincronizare completă'
        }
      case 'failed':
        return {
          icon: <ErrorIcon />,
          color: 'error' as const,
          label: 'Eroare',
          description: progress.error_message || 'A apărut o eroare la sincronizare'
        }
      default:
        return {
          icon: <ClockIcon />,
          color: 'default' as const,
          label: status,
          description: ''
        }
    }
  }

  // Calculate overall progress
  const calculateOverallProgress = (): number => {
    if (Object.keys(progresses).length === 0) return 0
    const total = Object.values(progresses).reduce((sum, p) => sum + (p.progress_percent || 0), 0)
    return Math.round(total / Object.keys(progresses).length)
  }

  // Calculate totals
  const calculateTotals = () => {
    let totalNew = 0, totalUpdated = 0, totalSkipped = 0, totalFailed = 0, totalProcessed = 0
    
    Object.values(progresses).forEach(p => {
      if (p.studies) {
        totalNew += p.studies.new || 0
        totalUpdated += p.studies.updated || 0
        totalSkipped += p.studies.skipped || 0
        totalFailed += p.studies.failed || 0
        totalProcessed += p.studies.processed || 0
      }
    })
    
    return { totalNew, totalUpdated, totalSkipped, totalFailed, totalProcessed }
  }

  const overallProgress = calculateOverallProgress()
  const totals = calculateTotals()

  return (
    <Dialog 
      open={open} 
      onClose={isCompleted ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!isCompleted}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {!isCompleted && <CircularProgress size={20} />}
          {isCompleted && (hasError ? <ErrorIcon color="error" /> : <CheckIcon color="success" />)}
          <Typography variant="h6">
            {isCompleted 
              ? (hasError ? 'Sincronizare finalizată cu erori' : 'Sincronizare finalizată') 
              : 'Sincronizare în curs...'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Overall Progress Bar */}
        {!isCompleted && (
          <Box mb={3}>
            <LinearProgress 
              variant="determinate" 
              value={overallProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              {overallProgress}% completat
            </Typography>
          </Box>
        )}

        {/* Summary Stats - doar când e finalizat */}
        {isCompleted && totals.totalProcessed > 0 && (
          <Box mb={2} p={2} sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Sumar sincronizare:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {totals.totalNew > 0 && (
                <Chip 
                  icon={<NewIcon />} 
                  label={`${totals.totalNew} studii noi`} 
                  size="small" 
                  color="success" 
                />
              )}
              {totals.totalUpdated > 0 && (
                <Chip 
                  icon={<UpdateIcon />} 
                  label={`${totals.totalUpdated} actualizate`} 
                  size="small" 
                  color="info" 
                />
              )}
              {totals.totalSkipped > 0 && (
                <Chip 
                  icon={<SkipIcon />} 
                  label={`${totals.totalSkipped} neschimbate`} 
                  size="small" 
                  color="default" 
                />
              )}
              {totals.totalFailed > 0 && (
                <Chip 
                  icon={<WarningIcon />} 
                  label={`${totals.totalFailed} erori`} 
                  size="small" 
                  color="error" 
                />
              )}
            </Box>
            {totals.totalNew === 0 && totals.totalUpdated === 0 && totals.totalSkipped > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Toate studiile erau deja sincronizate. Nu au fost necesare modificări.
              </Typography>
            )}
          </Box>
        )}

        {/* Individual Task Progress */}
        <List disablePadding>
          {taskIds.map(({ task_id, pacs_name }, index) => {
            const progress = progresses[task_id]
            
            // Loading state
            if (!progress) {
              return (
                <ListItem key={task_id} sx={{ py: 2 }}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <CircularProgress size={24} />
                    <Box>
                      <Typography variant="subtitle2">{pacs_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Se conectează...
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              )
            }

            const statusDisplay = getStatusDisplay(progress)

            return (
              <React.Fragment key={task_id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 2 }}>
                  <Box width="100%">
                    {/* Header: PACS Name + Status Chip */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">
                        {progress.pacs_name || pacs_name}
                        {progress.institution_name && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}({progress.institution_name})
                          </Typography>
                        )}
                      </Typography>
                      <Chip 
                        icon={statusDisplay.icon}
                        label={statusDisplay.label}
                        color={statusDisplay.color}
                        size="small"
                        variant={progress.status === 'completed' ? 'filled' : 'outlined'}
                      />
                    </Box>

                    {/* Status Description */}
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {statusDisplay.description}
                    </Typography>

                    {/* Progress Bar - doar când procesează */}
                    {(progress.status === 'processing' || progress.status === 'fetching') && (
                      <Box mb={1}>
                        <LinearProgress 
                          variant={progress.studies?.total ? "determinate" : "indeterminate"}
                          value={progress.progress_percent || 0} 
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                        {progress.studies?.total && (
                          <Typography variant="caption" color="text.secondary">
                            {progress.studies.processed || 0} din {progress.studies.total} studii procesate
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Current Step - când procesează */}
                    {progress.current_step && progress.status === 'processing' && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {progress.current_step
                          .replace(/Fetching study IDs page (\d+)/g, 'Preluare pagina $1...')
                          .replace(/Processing batch (\d+)\/(\d+)/g, 'Procesare lot $1/$2...')
                          .replace(/Marking historical studies/g, 'Marcare studii istorice...')
                        }
                      </Typography>
                    )}

                    {/* Time Info - când procesează */}
                    {progress.status === 'processing' && progress.performance?.estimated_time_remaining_seconds && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        ⏱️ Timp estimat rămas: ~{formatDuration(progress.performance.estimated_time_remaining_seconds)}
                      </Typography>
                    )}

                    {/* Duration - când e finalizat */}
                    {progress.status === 'completed' && progress.performance?.duration_seconds && (
                      <Typography variant="caption" color="success.main">
                        ✓ Completat în {formatDuration(progress.performance.duration_seconds)}
                      </Typography>
                    )}

                    {/* Error Message */}
                    {progress.error_message && (
                      <Alert severity="error" sx={{ mt: 1 }} variant="outlined">
                        {progress.error_message}
                      </Alert>
                    )}
                  </Box>
                </ListItem>
              </React.Fragment>
            )
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!isCompleted && (
          <Typography variant="caption" color="warning.main" sx={{ flex: 1 }}>
            Nu închideți fereastra până la finalizare
          </Typography>
        )}
        <Button 
          onClick={onClose} 
          variant={isCompleted ? "contained" : "outlined"}
          color={isCompleted ? "primary" : "inherit"}
          disabled={!isCompleted}
        >
          {isCompleted ? 'Închide' : 'Așteptați...'}
        </Button>
      </DialogActions>

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Dialog>
  )
}

export default SyncProgressDialog
